type Handlers = { enter: () => void; leave: () => void }

export class SwatchColourLabel extends HTMLElement {
  label: HTMLElement | null = null
  original = ''
  handlers = new Map<Element, Handlers>()

  strip: HTMLElement | null = null
  track: HTMLElement | null = null
  thumb: HTMLElement | null = null

  private dragging = false
  private pointerStartX = 0
  private scrollStart = 0

  connectedCallback() {
    this.initColourLabel()
    this.initScrollIndicator()
  }

  disconnectedCallback() {
    this.handlers.forEach(({ enter, leave }, el) => {
      el.removeEventListener('mouseenter', enter)
      el.removeEventListener('mouseleave', leave)
    })
    this.handlers.clear()

    this.strip?.removeEventListener('scroll', this.handleScroll)
    this.track?.removeEventListener('pointerdown', this.handlePointerDown)
    this.track?.removeEventListener('pointermove', this.handlePointerMove)
    this.track?.removeEventListener('pointerup', this.endDrag)
    this.track?.removeEventListener('pointercancel', this.endDrag)
    window.removeEventListener('resize', this.measure)
  }

  private initColourLabel() {
    this.label = this.querySelector('#product-colour-name')
    if (!this.label) return

    this.original = this.label.textContent ?? ''

    this.querySelectorAll<HTMLElement>('[data-colour-name]').forEach(el => {
      const colourName = el.dataset.colourName ?? this.original
      const enter = () => { this.label!.textContent = colourName }
      const leave = () => { this.label!.textContent = this.original }
      el.addEventListener('mouseenter', enter)
      el.addEventListener('mouseleave', leave)
      this.handlers.set(el, { enter, leave })
    })
  }

  private initScrollIndicator() {
    this.strip = this.querySelector('[data-swatch-strip]')
    this.track = this.querySelector('[data-swatch-track]')
    this.thumb = this.querySelector('[data-swatch-thumb]')
    if (!this.strip || !this.thumb || !this.track) return

    this.strip.addEventListener('scroll', this.handleScroll, { passive: true })
    window.addEventListener('resize', this.measure)

    // Pointer capture means move/up keep firing on the track once the drag starts,
    // even when the cursor wanders off it — so no window-level listeners are needed.
    this.track.addEventListener('pointerdown', this.handlePointerDown)
    this.track.addEventListener('pointermove', this.handlePointerMove)
    this.track.addEventListener('pointerup', this.endDrag)
    this.track.addEventListener('pointercancel', this.endDrag)

    // Deferred a frame so the grid track has settled on its final width.
    requestAnimationFrame(() => {
      this.measure()
      this.revealActiveSwatch()
    })
  }

  /**
   * Brings the viewed product's swatch into view.
   *
   * A no-op for the carousel, which renders the viewed colour first and so already shows it
   * at scroll 0 — this is the backstop for a short row that keeps its metafield order and
   * still overflows on a narrow viewport (five 44px swatches exceed the track at 375px).
   *
   * Deliberately not `scrollIntoView()`: that walks every scrollable ancestor and would
   * jump the whole page down to the swatch row on load. Scrolls the minimum distance, so
   * a swatch that is already visible is left exactly where it is.
   */
  private revealActiveSwatch() {
    const { strip } = this
    if (!strip) return

    const active = strip.querySelector<HTMLElement>('[data-current-swatch]')
    if (!active) return

    const stripBox = strip.getBoundingClientRect()
    const activeBox = active.getBoundingClientRect()
    if (activeBox.left >= stripBox.left && activeBox.right <= stripBox.right) return

    const delta = activeBox.left < stripBox.left
      ? activeBox.left - stripBox.left
      : activeBox.right - stripBox.right

    // scroll-smooth would animate this correction on first paint.
    const behaviour = strip.style.scrollBehavior
    strip.style.scrollBehavior = 'auto'
    strip.scrollLeft += delta
    strip.style.scrollBehavior = behaviour
  }

  // Arrow properties, not constructor-bound methods: removeEventListener only
  // matches on identity, and a fresh `.bind(this)` per call would never match.
  //
  // Whether the strip scrolls is a measurement, not a swatch count — five 44px
  // swatches fit the desktop track but overflow it at 375px — so the indicator
  // is shown or hidden on what the element actually measures.
  private measure = () => {
    const { strip, track, thumb } = this
    if (!strip || !track || !thumb) return

    const overflow = strip.scrollWidth - strip.clientWidth
    track.classList.toggle('hidden', overflow <= 1)
    if (overflow <= 1) return

    thumb.style.width = `${(strip.clientWidth / strip.scrollWidth) * 100}%`
    this.handleScroll()
  }

  private handleScroll = () => {
    const { strip, thumb } = this
    if (!strip || !thumb) return

    const max = strip.scrollWidth - strip.clientWidth
    if (max <= 0) return

    // translateX resolves against the thumb's own width, so a full sweep is the
    // leftover track measured in thumb widths rather than a flat 100%.
    const ratio = strip.clientWidth / strip.scrollWidth
    const progress = strip.scrollLeft / max
    thumb.style.transform = `translateX(${progress * (100 / ratio - 100)}%)`
  }

  /** Free travel available to the thumb, in px. 0 when there is nothing to scroll. */
  private freeTravel() {
    const { track, thumb } = this
    if (!track || !thumb) return 0
    return track.clientWidth - thumb.clientWidth
  }

  private handlePointerDown = (event: PointerEvent) => {
    const { strip, track, thumb } = this
    if (!strip || !track || !thumb) return
    // Touch keeps the native swipe — a hairline is not a touch target, and
    // capturing the gesture here would fight the strip's own scrolling.
    if (!window.matchMedia('(pointer: fine)').matches) return
    if (this.freeTravel() <= 0) return

    // Pressing the bare track jumps the thumb under the cursor first, then drags
    // on from there, which is how a real scrollbar behaves.
    if (event.target !== thumb) this.scrollToPointer(event.clientX)

    this.dragging = true
    this.pointerStartX = event.clientX
    this.scrollStart = strip.scrollLeft
    track.setPointerCapture(event.pointerId)
    // scroll-smooth would animate every frame of the drag, which reads as lag.
    strip.style.scrollBehavior = 'auto'
    event.preventDefault()
  }

  private handlePointerMove = (event: PointerEvent) => {
    const { strip } = this
    if (!this.dragging || !strip) return

    const free = this.freeTravel()
    if (free <= 0) return

    const max = strip.scrollWidth - strip.clientWidth
    strip.scrollLeft = this.scrollStart + ((event.clientX - this.pointerStartX) / free) * max
  }

  private endDrag = (event: PointerEvent) => {
    if (!this.dragging) return
    this.dragging = false
    if (this.track?.hasPointerCapture(event.pointerId)) {
      this.track.releasePointerCapture(event.pointerId)
    }
    if (this.strip) this.strip.style.scrollBehavior = ''
  }

  /** Centres the thumb on `clientX`, clamped to the track. */
  private scrollToPointer(clientX: number) {
    const { strip, track, thumb } = this
    if (!strip || !track || !thumb) return

    const free = this.freeTravel()
    if (free <= 0) return

    const offset = clientX - track.getBoundingClientRect().left - thumb.clientWidth / 2
    const max = strip.scrollWidth - strip.clientWidth
    strip.scrollLeft = (Math.min(Math.max(offset, 0), free) / free) * max
  }
}
